import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export interface DockNotification {
  id: string;
  title: string;
  body?: string;
  tone?: "info" | "success" | "warn" | "danger";
  ttl?: number; // ms
}

const EVT = "ss2026:notify";

export function notify(n: Omit<DockNotification, "id">) {
  if (typeof window === "undefined") return;
  const full: DockNotification = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    ttl: 4200,
    tone: "info",
    ...n,
  };
  window.dispatchEvent(new CustomEvent<DockNotification>(EVT, { detail: full }));
}

const TONE: Record<NonNullable<DockNotification["tone"]>, string> = {
  info: "border-border/60",
  success: "border-[oklch(0.82_0.24_145)]/50",
  warn: "border-amber-400/50",
  danger: "border-primary/60",
};

/**
 * Non-intrusive Notification Dock.
 * - Mobile: bottom, above BottomNav (clears safe-area + 80px nav height).
 * - Desktop: bottom-right.
 * - Max 2 stacked notifications, auto-dismiss with fade.
 */
export function NotificationDock() {
  const [items, setItems] = useState<DockNotification[]>([]);
  const { t } = useLanguage();

  useEffect(() => {
    const onNotify = (e: Event) => {
      const n = (e as CustomEvent<DockNotification>).detail;
      if (!n) return;
      setItems((prev) => [...prev.slice(-1), n]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== n.id));
      }, n.ttl ?? 4200);
    };
    window.addEventListener(EVT, onNotify);
    return () => window.removeEventListener(EVT, onNotify);
  }, []);

  if (!items.length) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed z-40 flex flex-col gap-2 px-3 w-[min(92vw,360px)]
                 bottom-[calc(env(safe-area-inset-bottom,0px)+72px)] left-1/2 -translate-x-1/2
                 lg:left-auto lg:right-4 lg:bottom-6 lg:translate-x-0"
    >
      {items.map((n) => (
        <div
          key={n.id}
          className={`pointer-events-auto overflow-hidden rounded-2xl border ${TONE[n.tone ?? "info"]} glass px-3 py-2 shadow-[0_20px_60px_-15px_oklch(0_0_0/0.6)] animate-fade-in`}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold leading-tight text-foreground truncate">
                {n.title}
              </div>
              {n.body && (
                <div className="mt-0.5 text-[10px] leading-tight text-muted-foreground line-clamp-2">
                  {n.body}
                </div>
              )}
            </div>
            <button
              onClick={() => setItems((p) => p.filter((x) => x.id !== n.id))}
              aria-label={t("close")}
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
